import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { DeliveryService, DeliveryOutputDto } from 'src/generated_client';

export interface FixedPointShiftSession {
  id: number;
  cdDelivery: number;
  startTime: string;
  endTime: string | null;
  deliveryName: string;
  deliveryPhone: string;
  client: string;
  hours: number;
}

@Component({
  selector: 'app-delivery-records',
  templateUrl: './delivery-records.component.html',
  styleUrls: ['./delivery-records.component.css'],
})
export class DeliveryRecordsComponent implements OnInit {
  private readonly argentinaTimeZone = 'America/Argentina/Buenos_Aires';


  // Filter properties
  filterDateFrom: Date | null = null;
  filterDateTo: Date | null = null;
  filterCadete: string | null = null;
  filterClientName: string | null = null;
  filterIsFinished: boolean | null = null;
  cadetes: DeliveryOutputDto[] = [];

  displayedColumns: string[] = [
    'deliveryName',
    'deliveryPhone',
    'client',
    'startTime',
    'endTime',
    'actions',
  ];
  dataSource: FixedPointShiftSession[] = [];

  get safeCadetes(): DeliveryOutputDto[] {
    return Array.isArray(this.cadetes) ? this.cadetes : [];
  }

  get safeDisplayedColumns(): string[] {
    return Array.isArray(this.displayedColumns) ? this.displayedColumns : [];
  }

  get safeDataSource(): FixedPointShiftSession[] {
    return Array.isArray(this.dataSource) ? this.dataSource : [];
  }

  private defaultHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  });

  constructor(
    private dialog: MatDialog,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private deliveryService: DeliveryService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.fetchShiftSessions();
    this.loadCadetesForFilter();
  }

  fetchShiftSessions(): void {
    const url = `${environment.apiBaseUrl}/api/WorkShiftSession/GetAll`;
    
    // Build query parameters based on filters
    let params = new HttpParams();
    
    params = params.set('pageNumber', '1');
    params = params.set('pageSize', '50');

    if (this.filterDateFrom) {
      // Format date as YYYY-MM-DD for the backend
      const fromDate = new Date(this.filterDateFrom);
      params = params.set('from', fromDate.toISOString().split('T')[0]);
    }
    
    if (this.filterDateTo) {
      // Format date as YYYY-MM-DD for the backend
      const toDate = new Date(this.filterDateTo);
      params = params.set('to', toDate.toISOString().split('T')[0]);
    }
    
    if (this.filterCadete && this.filterCadete !== 'null') {
      params = params.set('cdDelivery', this.filterCadete);
    }

    if (this.filterClientName && this.filterClientName.trim()) {
      params = params.set('clientName', this.filterClientName.trim());
    }

    this.http.get<unknown>(url, {
      params: params 
    })
      .subscribe({
        next: (response: unknown) => {
          const sessions = this.extractArrayFromResponse<FixedPointShiftSession>(response, 'shiftSessions');

          const mappedSessions = sessions.map(s => ({
            ...s,
            deliveryName: s.deliveryName,
            deliveryPhone: s.deliveryPhone,
            client: s.client
          }));

          this.dataSource = this.applyFinishedFilter(mappedSessions);
        },
        error: (err) => {
          console.error('Error loading shift sessions', err);
          this.snackBar.open('Error al cargar los registros de delivery', 'Cerrar', {
            duration: 3000,
          });
        },
      });
  }

  onEndShift(session: FixedPointShiftSession): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: {
        message: '¿Está seguro que desea finalizar este turno?',
        confirmButtonText: 'Finalizar',
        cancelButtonText: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.endShift(session.id);
      }
    });
  }

  private endShift(sessionId: number): void {
    const url = `${environment.apiBaseUrl}/api/WorkShiftSession/EndShift/${sessionId}`;
    
    this.http.post(url, {}, { headers: this.defaultHeaders })
      .subscribe({
        next: () => {
          const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            width: '350px',
            data: {
              message: 'Turno finalizado correctamente',
              confirmButtonText: 'Aceptar',
              cancelButtonText: '',
              showCancelButton: false,
            },
          });
          dialogRef.afterClosed().subscribe(() => {
            this.fetchShiftSessions();
          });
        },
        error: (err) => {
          console.error('Error ending shift', err);
          const message = err?.error?.error || 'Ocurrió un error al finalizar el turno';
          this.dialog.open(ConfirmDialogComponent, {
            width: '350px',
            data: {
              message,
              confirmButtonText: 'Aceptar',
              showCancelButton: false,
            },
          });
        }
      });
  }

  loadCadetesForFilter(): void {
    this.deliveryService.apiDeliveryGetAllGet().subscribe({
      next: (response: unknown) => {
        this.cadetes = this.extractArrayFromResponse<DeliveryOutputDto>(response, 'cadetes');
      },
      error: (err: any) => {
        this.cadetes = [];
        console.error('Error al cargar cadetes', err);
      }
    });
  }

  private extractArrayFromResponse<T>(response: unknown, source: string): T[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (!response || typeof response !== 'object') {
      return [];
    }

    const candidate = response as {
      data?: unknown;
      items?: unknown;
      results?: unknown;
      result?: unknown;
      value?: unknown;
      values?: unknown;
      $values?: unknown;
      records?: unknown;
      rows?: unknown;
      content?: unknown;
    };

    const nestedCollections = [
      candidate.data,
      candidate.items,
      candidate.results,
      candidate.result,
      candidate.value,
      candidate.values,
      candidate.$values,
      candidate.records,
      candidate.rows,
      candidate.content,
    ];

    for (const nestedCollection of nestedCollections) {
      if (Array.isArray(nestedCollection)) {
        return nestedCollection;
      }

      if (nestedCollection && typeof nestedCollection === 'object') {
        const nestedArray = this.extractArrayFromResponse<T>(nestedCollection, source);
        if (nestedArray.length > 0) {
          return nestedArray;
        }
      }
    }

    return [];
  }

  clearFilters(): void {
    this.filterDateFrom = null;
    this.filterDateTo = null;
    this.filterCadete = null;
    this.filterClientName = null;
    this.filterIsFinished = null;
    this.fetchShiftSessions();
  }

  private applyFinishedFilter(sessions: FixedPointShiftSession[]): FixedPointShiftSession[] {
    if (this.filterIsFinished === null) {
      return sessions;
    }

    return sessions.filter(session => this.filterIsFinished ? !!session.endTime : !session.endTime);
  }

  onEditShift(session: FixedPointShiftSession): void {
    this.router.navigate(['/edit-shift-session', session.id]);
  }

  formatUtcToArgentina(dateValue: string | null): string {
    if (!dateValue) {
      return 'En curso';
    }

    const parsedDate = this.parseUtcDate(dateValue);
    if (!parsedDate) {
      return dateValue;
    }

    return new Intl.DateTimeFormat('es-AR', {
      timeZone: this.argentinaTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(parsedDate);
  }

  private parseUtcDate(dateValue: string): Date | null {
    const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(dateValue);
    const utcDateValue = hasTimezone ? dateValue : `${dateValue}Z`;
    const parsedDate = new Date(utcDateValue);

    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    return parsedDate;
  }
}
